<template>
    <v-menu
        v-model="abierto"
        :close-on-content-click="false"
        offset-y
        bottom
        left
        nudge-bottom="10"
        max-width="400"
        min-width="320">
        <template v-slot:activator="{ on, attrs }">
            <v-btn
                icon
                class="mr-1"
                v-bind="attrs"
                v-on="on"
                :title="noLeidas ? `${noLeidas} notificaciones sin leer` : 'Notificaciones'"
                aria-label="Notificaciones">
                <v-badge
                    :content="textoBadge"
                    :value="noLeidas > 0"
                    color="#D29E4A"
                    overlap
                    bordered>
                    <v-icon size="26">{{ noLeidas ? 'notifications_active' : 'notifications' }}</v-icon>
                </v-badge>
            </v-btn>
        </template>

        <v-card class="campana">
            <div class="campana__cabecera d-flex align-center px-4 py-2">
                <span class="text-subtitle-2 font-weight-bold">Notificaciones</span>
                <v-chip v-if="noLeidas" x-small label class="ml-2" color="#D29E4A" text-color="white">{{ noLeidas }} sin leer</v-chip>
                <v-spacer></v-spacer>
                <v-btn
                    x-small
                    text
                    color="primary"
                    class="text-none"
                    :disabled="!noLeidas || marcando"
                    :loading="marcando"
                    @click="marcarTodas">
                    <v-icon x-small left>done_all</v-icon>Marcar todas como leídas
                </v-btn>
            </div>
            <v-divider></v-divider>

            <v-progress-linear v-if="cargando && !notificaciones.length" indeterminate color="primary" height="2"></v-progress-linear>

            <v-list v-if="notificaciones.length" dense class="campana__lista py-0">
                <template v-for="(n, indice) in notificaciones">
                    <v-list-item
                        :key="n.notificacion_id"
                        :class="{'campana__item--nueva': !n.leida}"
                        class="campana__item"
                        @click="abrir(n)">
                        <v-list-item-avatar size="32" class="my-2 mr-3" :color="colorTipo(n.tipo)">
                            <v-icon small dark>{{ iconoTipo(n.tipo) }}</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content class="py-2">
                            <v-list-item-title class="campana__titulo" :class="{'font-weight-bold': !n.leida}">{{ n.titulo }}</v-list-item-title>
                            <div class="campana__mensaje">{{ n.mensaje }}</div>
                            <div class="text-caption grey--text text--darken-1 mt-1">
                                {{ haceCuanto(n.fecha_crea) }}
                                <span v-if="n.enlace" class="primary--text"> · Abrir</span>
                            </div>
                        </v-list-item-content>
                        <v-list-item-action v-if="!n.leida" class="my-0 ml-1 align-self-center">
                            <span class="campana__punto"></span>
                        </v-list-item-action>
                    </v-list-item>
                    <v-divider v-if="indice < notificaciones.length - 1" :key="'d' + n.notificacion_id"></v-divider>
                </template>
            </v-list>

            <div v-else-if="!cargando" class="campana__vacio text-center px-6 py-8">
                <v-icon size="40" color="grey lighten-1">notifications_none</v-icon>
                <div class="text-body-2 grey--text text--darken-1 mt-2">No tiene notificaciones.</div>
            </div>

            <v-divider></v-divider>
            <div class="px-4 py-2 text-caption grey--text text--darken-1 d-flex align-center">
                <span>Se muestran las últimas {{ notificaciones.length }}.</span>
                <v-spacer></v-spacer>
                <v-btn x-small text class="text-none" :loading="cargando" @click="consultar(true)">
                    <v-icon x-small left>refresh</v-icon>Actualizar
                </v-btn>
            </div>
        </v-card>
    </v-menu>
</template>

<script>
const ICONO_TIPO = {certificado: "workspace_premium", mensaje: "campaign", sistema: "settings"};
const COLOR_TIPO = {certificado: "#D29E4A", mensaje: "#203D89", sistema: "grey darken-1"};

/** Cada cuánto se vuelve a consultar el API (90 segundos). */
const INTERVALO_MS = 90000;

/**
 * Campana del encabezado: badge con las notificaciones sin leer y menú con
 * las últimas. Consulta al montar, al cambiar de ruta y cada 90 segundos;
 * también cuando otra pantalla emite "notificaciones-actualizar".
 */
export default {
    name: "CampanaNotificaciones",
    data() {
        return {
            abierto: false,
            cargando: false,
            marcando: false,
            notificaciones: [],
            noLeidas: 0,
            temporizador: null,
        }
    },
    computed: {
        textoBadge() {
            return this.noLeidas > 99 ? "99+" : String(this.noLeidas);
        },
    },
    watch: {
        "$route.path"() {
            this.consultar();
        },
    },
    methods: {
        iconoTipo(tipo) {
            return ICONO_TIPO[tipo] || "notifications";
        },
        colorTipo(tipo) {
            return COLOR_TIPO[tipo] || "grey";
        },
        /** Texto relativo en español: hace un momento, hace 5 min, hace 3 h, ayer, hace 4 días o la fecha. */
        haceCuanto(fecha) {
            if (!fecha) return "";
            const momento = new Date(String(fecha).replace(/-/g, "/")).getTime();
            if (Number.isNaN(momento)) return "";

            const segundos = Math.max(0, Math.round((Date.now() - momento) / 1000));
            if (segundos < 60) return "hace un momento";
            const minutos = Math.round(segundos / 60);
            if (minutos < 60) return `hace ${minutos} min`;
            const horas = Math.round(minutos / 60);
            if (horas < 24) return `hace ${horas} h`;
            const dias = Math.round(horas / 24);
            if (dias === 1) return "ayer";
            if (dias < 30) return `hace ${dias} días`;

            return this.$options.filters.fechaESP(fecha);
        },
        /** Consulta el API; silenciosa salvo que el usuario la pida (forzar). */
        consultar(forzar = false) {
            if (this.cargando) return;
            if (!forzar && typeof document !== "undefined" && document.hidden) return;

            this.cargando = true;
            window.axios.get("/notificaciones/mias").then(response => {
                if (response.data.success && response.data.data) {
                    this.notificaciones = response.data.data.notificaciones || [];
                    this.noLeidas = Number(response.data.data.no_leidas) || 0;
                }
            })
                .catch(() => {
                    if (forzar) this.$toast.error("No se pudieron consultar las notificaciones.");
                })
                .finally(() => {
                    this.cargando = false;
                });
        },
        /** Marca una como leída y navega a su enlace si lo tiene. */
        abrir(n) {
            if (!n.leida) {
                n.leida = true;
                this.noLeidas = Math.max(0, this.noLeidas - 1);
                window.axios.post("/notificaciones/leer", {ids: [n.notificacion_id]}).then(response => {
                    if (response.data.success && response.data.data) {
                        this.noLeidas = Number(response.data.data.no_leidas) || 0;
                    }
                }).catch(() => {});
            }

            if (!n.enlace) return;

            this.abierto = false;
            if (/^https?:\/\//i.test(n.enlace)) {
                window.open(n.enlace, "_blank", "noopener");
            } else if (this.$route.fullPath !== n.enlace) {
                this.$router.push(n.enlace).catch(() => {});
            }
        },
        marcarTodas() {
            this.marcando = true;
            window.axios.post("/notificaciones/leer", {todas: true}).then(response => {
                if (response.data.success) {
                    this.notificaciones.forEach(n => {
                        n.leida = true;
                    });
                    this.noLeidas = 0;
                } else {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(() => this.$toast.error("No se pudieron marcar las notificaciones."))
                .finally(() => {
                    this.marcando = false;
                });
        },
    },
    mounted() {
        this.consultar();
        this.temporizador = setInterval(() => this.consultar(), INTERVALO_MS);
        this.$root.$on("notificaciones-actualizar", this.consultar);
    },
    beforeDestroy() {
        clearInterval(this.temporizador);
        this.$root.$off("notificaciones-actualizar", this.consultar);
    },
}
</script>

<style lang="scss" scoped>
.campana {
    width: 400px;
    max-width: 100vw;

    &__cabecera {
        min-height: 44px;
    }

    &__lista {
        max-height: 60vh;
        overflow-y: auto;
    }

    &__item {
        min-height: 56px;
    }

    &__item--nueva {
        background-color: #f3f5fb;
    }

    &__titulo {
        font-size: .875rem;
        white-space: normal;
        line-height: 1.3;
    }

    &__mensaje {
        font-size: .8125rem;
        color: #5b6380;
        line-height: 1.35;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    &__punto {
        display: inline-block;
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background-color: #D29E4A;
    }
}
</style>
